import { __ } from '../../../../utils';
import {
  Box,
  Button,
  DataTable,
  Stack,
  TextField,
  Typography,
  type Column,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type PendingFile = { id: string; file: File };

type Props = {
  hint: string;
  /**
   * Prepínač „označiť nález ako vyriešený". Nepatrí medzi kroky rozhodnutia,
   * ale sem, nad text: je to voľba o tom, **čím to vyjadrenie je** - priebežnou
   * poznámkou alebo finálnym stanoviskom, ktorým sa nález uzatvára.
   */
  resolveSlot?: React.ReactNode;
  text: string;
  onTextChange: (text: string) => void;
  files: PendingFile[];
  onFilesChange: (files: PendingFile[]) => void;
  compact?: boolean;
  /**
   * Vlastný nadpis „Vyjadrenie k nálezu". Na desktope ho nesie karta sekcie
   * ([[IssueSection]]), ktorá formulár obaľuje, takže sa vypína.
   */
  showHeading?: boolean;
};

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

const formatCount = (count: number) =>
  `${count} ${count === 1 ? __('súbor') : count <= 4 ? __('súbory') : __('súborov')}`;

const formatKind = (type: string) => {
  const [, subtype = ''] = type.split('/');
  return subtype.toUpperCase().slice(0, 4);
};

/**
 * Formulár vyjadrenia k nálezu.
 *
 * Hotové vyjadrenia tu **nie sú**: všetky, aj finálne, kreslí časová os
 * ([IssueStatementHistory](IssueStatementHistory.tsx)), zbalená pod rozhodovaním. Táto
 * sekcia je odteraz len to, čo úradník píše teraz - pri uzavretom náleze sa
 * nevykreslí vôbec.
 *
 * Prílohy sa držia **lokálne** a odosielajú sa až po tom, ako `decision`
 * vytvorí vyjadrenie a vráti jeho id. Je to o jedno volanie viac, ale
 * rozhodnutie tým zostáva jednou transakciou: multipart by znamenal booleany
 * a polia ako stringy a validáciu vlastnou rukou.
 */
const IssueStatementSection: React.FC<Props> = ({
  hint,
  resolveSlot,
  text,
  onTextChange,
  files,
  onFilesChange,
  compact = false,
  showHeading = true,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const columns: Column<PendingFile>[] = [
    {
      key: 'kind',
      label: '',
      width: '46px',
      render: (row) => (
        <Typography variant='caption' fontWeight={700} color='text.secondary'>
          {formatKind(row.file.type)}
        </Typography>
      ),
    },
    {
      key: 'name',
      label: __('Názov'),
      width: '2fr',
      strong: true,
      render: (row) => row.file.name,
    },
    {
      key: 'type',
      label: __('Typ'),
      width: '1fr',
      render: (row) => row.file.type,
    },
    {
      key: 'size',
      label: __('Veľkosť'),
      width: '0.8fr',
      numeric: true,
      mono: true,
      render: (row) => formatSize(row.file.size),
    },
    {
      key: 'action',
      label: '',
      width: '110px',
      render: (row) => (
        <Button
          variant='text'
          size='small'
          onClick={() => onFilesChange(files.filter((f) => f.id !== row.id))}
        >
          {__('Odstrániť')}
        </Button>
      ),
    },
  ];

  return (
    <Stack gap={1.25}>
      {showHeading && (
        <Stack direction='row' alignItems='baseline' gap={1} flexWrap='wrap'>
          <Typography variant='body2' fontWeight={500}>
            {__('Vyjadrenie k nálezu')}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {hint}
          </Typography>
        </Stack>
      )}

      {resolveSlot}

      <TextField
        multiline
        minRows={compact ? 4 : 3}
        fullWidth
        value={text}
        onChange={(event) => onTextChange(event.target.value)}
        placeholder={__('Text vyjadrenia sa zverejní pri podnete…')}
      />

      <input
        ref={inputRef}
        type='file'
        multiple
        hidden
        onChange={(event) => {
          const picked = Array.from(event.target.files ?? []);
          onFilesChange([
            ...files,
            ...picked.map((file) => ({
              id: `${file.name}-${file.size}-${file.lastModified}`,
              file,
            })),
          ]);
          event.target.value = '';
        }}
      />

      <Stack direction='row' alignItems='center' gap={1.25}>
        <Typography variant='caption' fontWeight={500} color='text.primary'>
          {__('Prílohy vyjadrenia')}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {files.length ? formatCount(files.length) : __('žiadne')}
        </Typography>
        {/* `outline` z dizajn systému, nie MUI `outlined`/`text`: červená
              patrí potvrdzovaciemu tlačidlu v pätičke, nie výberu súboru. */}
        <Button
          variant='outline'
          size='small'
          sx={{ ml: 'auto' }}
          onClick={() => inputRef.current?.click()}
        >
          {__('Nahrať prílohu')}
        </Button>
      </Stack>

      {files.length > 0 &&
        (compact ? (
          <Stack gap={1}>
            {files.map((row) => (
              <Stack
                key={row.id}
                direction='row'
                alignItems='center'
                gap={1.25}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 24,
                    flex: 'none',
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: 0.5,
                    backgroundColor: 'neutralSurface',
                  }}
                >
                  <Typography variant='caption' fontWeight={700}>
                    {formatKind(row.file.type)}
                  </Typography>
                </Box>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant='body2' fontWeight={500} noWrap>
                    {row.file.name}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {`${row.file.type} · ${formatSize(row.file.size)}`}
                  </Typography>
                </Stack>
                <Button
                  variant='text'
                  size='small'
                  onClick={() =>
                    onFilesChange(files.filter((f) => f.id !== row.id))
                  }
                >
                  {__('Odstrániť')}
                </Button>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <DataTable
              columns={columns}
              rows={files}
              rowKey='id'
              minWidth={560}
            />
          </Box>
        ))}
    </Stack>
  );
};

export default IssueStatementSection;
export type { PendingFile };
